<template>
    <section v-editable="blok" class="tcp-section airport-page-section">
        <div class="tcp-section__container md:container">
            <h2 class="tcp-section__title airport-page-title">
                {{ blok.title }}
            </h2>
            <RichText :richtext="blok.description" class="tcp-section__descr" />
            <div class="tcp-section__carousel">
                <Carousel
                    v-if="topRatedProducts?.length"
                    ref="carparksCarousel"
                    v-model="currentSlide"
                    :settings="settings"
                    :breakpoints="breakpoints"
                >
                    <Slide v-for="product in topRatedProducts" :key="product.id">
                        <CarparkCard :car-park="product" />
                    </Slide>
                    <template #addons>
                        <Pagination :paginate-by-items-to-show="false" />
                    </template>
                </Carousel>

                <div v-else-if="!topRatedProducts?.length && loadingProducts" class="loading-products">
                    <NuxtIcon name="ri:loader-line" class="animate-spin" />
                </div>

                <div v-else class="no-products">{{ $t('carParks.noProductsAvailable') }}</div>

                <SliderBtn
                    btn-type="prev"
                    class="tcp-section__carousel-arrow tcp-section__carousel-arrow--left"
                    :class="{ 'is-active': showPrev }"
                    aria-label="Navigate to previous car park"
                    @click="prev"
                />
                <SliderBtn
                    btn-type="next"
                    class="tcp-section__carousel-arrow tcp-section__carousel-arrow--right"
                    :class="{ 'is-active': showNext }"
                    aria-label="Navigate to next car park"
                    @click="next"
                />
            </div>
            <div
                v-if="fetchedProductsFailed.length"
                class="inline-block bg-red-400 rounded mt-4 px-2 py-1 text-white float-right"
            >
                <NuxtIcon name="ri:error-warning-line" class="mr-1" />
                [ DEV ] Products failed to load: {{ fetchedProductsFailed.join(', ') }}
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
// @ts-ignore
import { Carousel, Slide, Pagination } from 'vue3-carousel';
import 'vue3-carousel/dist/carousel.css';
import type { IL4TopRatedCarParks } from '~/types/components/L4TopRatedCarParks';
import type { IL4ApiProduct, IL4ApiProductFull } from '~/types/components/L4Products';
import type { IFeefoParkingProduct } from '~/types/api/FeefoReviews';

const { blok } = defineProps<{ blok: IL4TopRatedCarParks }>();

// Constant variable for access to Runtime Config
const config = useRuntimeConfig();
const { locale, locales } = useI18n();
const { feefoMerchantId } = useFeefo();

const apiProductList = inject<Ref<IL4ApiProduct[]>>('apiParkingProducts');
const loadingProducts = ref(true);

const fetchedProducts = ref([]);
const fetchedProductsFailed = ref([]);
const productIdsArr = blok.productIds
    ?.replace(/\s+/g, '')
    .split(',')
    .filter((item) => !!item);

onMounted(async () => {
    // Use list of product ids from Storyblok if provided
    if (productIdsArr?.length) {
        for (const productId of productIdsArr) {
            // get product data from api
            try {
                await $privateApi<IL4ApiProductFull>(`parking/products/public/${productId}`, {
                    method: 'GET',
                    params: {
                        lang: locales.value.filter((e) => e.code === locale.value)[0].storyblok,
                    },
                    async onResponse({ response }) {
                        if (response.ok) {
                            const product = response._data;
                            // get product rating from Feefo API
                            const productRating = await getProductRating(Number(productId));
                            fetchedProducts.value.push({ ...product, rating: productRating?.rating?.rating });
                        }
                        loadingProducts.value = false;
                    },
                    onResponseError({ response }) {
                        if (response) {
                            console.error(response._data);
                            if (config?.public?.STORYBLOK_VERSION === 'draft') {
                                fetchedProductsFailed.value.push(productId);
                            }
                        }
                    },
                });
            } catch (error) {
                console.error(`Failed to load product with id: ${productId}`, `error: ${error}`);
            }
        }
    } else if (apiProductList) {
        // if productIds field is empty - use data from L4 api
        for (const product of apiProductList.value ?? []) {
            // get product rating from Feefo API
            const productRating = await getProductRating(product.id);
            fetchedProducts.value.push({ ...product, rating: productRating?.rating?.rating });
        }
    }
});

async function getProductRating(productId: number) {
    return await $fetch<IFeefoParkingProduct>(
        `https://api.feefo.com/api/20/reviews/summary/product?since_period=All&unanswered_feedback=include&product_sku=${productId}&merchant_identifier=${feefoMerchantId}`
    );
}

const topRatedProducts = computed(() => {
    //sort products based on rating
    const sorted = [...fetchedProducts.value].sort((prev, next) => {
        return next.rating - prev.rating;
    });
    return productIdsArr?.length ? sorted : sorted.slice(0, 6);
});

const carparksCarousel = ref<null | object>(null);
const currentSlide = ref<number>(0);

// @ts-ignore
const next = () => carparksCarousel?.value.next();
// @ts-ignore
const prev = () => carparksCarousel?.value.prev();

const showNext = computed(
    () =>
        currentSlide.value <
        // @ts-ignore
        topRatedProducts.value.length - carparksCarousel?.value?.data?.config?.itemsToShow
);
const showPrev = computed(() => currentSlide.value !== 0);

const settings = ref({
    itemsToShow: 1,
    snapAlign: 'center',
});
const breakpoints = ref({
    // 768px and up
    768: {
        itemsToShow: 2,
        snapAlign: 'center',
    },
    // 1050 and up
    1050: {
        itemsToShow: 3,
        snapAlign: 'start',
    },
});
</script>

<style lang="postcss" scoped>
.tcp-section {
    &__descr {
        @apply mb-6;
        :deep(p) {
            @apply md:text-xl;
        }
    }
    &__carousel {
        @apply relative -mx-3 -mb-2;
        &-arrow {
            @apply absolute opacity-0 pointer-events-none top-40;
            &.is-active {
                @apply opacity-100 pointer-events-auto;
            }
            &--right {
                @apply -right-1 md:-right-3;
            }
            &--left {
                @apply -left-1 md:-left-3;
            }
        }
    }
}
.carousel__slide {
    @apply px-3 py-2;
    & > article {
        width: 100%;
        height: 100%;
    }
}
</style>

<style lang="postcss">
.tcp-section__carousel {
    .carousel__pagination {
        @apply md:hidden;
    }
    .carousel__pagination-button {
        @apply p-2;
        @apply h-2 w-2 rounded-full bg-[#D1D5DA];
        &--active {
            @apply bg-primary;
        }
    }
}
.no-products,
.loading-products {
    @apply text-center text-base my-4;
}

.loading-products {
    @apply text-3xl;
}
</style>
